import {
  Flex,
  VStack,
  Button,
  IconButton,
  Text,
  Box,
  HStack,
  useColorMode,
  Skeleton,
} from "@chakra-ui/react";
import React from "react";

import {
  FiUserCheck,
  FiBarChart2,
  FiLogOut,
  FiChevronDown,
  FiChevronRight,
  FiChevronUp,
  FiBriefcase,
  FiLayout,
  FiRepeat,
  FiCheckSquare,
  FiMessageSquare,
  FiHelpCircle,
} from "react-icons/fi";
import { HiOutlineClipboardList } from "react-icons/hi";
import { MdLocalFireDepartment, MdHistory } from "react-icons/md";
import {
  FiUsers, // Users
  FiLayers, // Projects (similar to stack/layers)
  FiList, // Tasks (checklist)
  FiClock, // TimeSheets (clock)
  FiTrendingUp, // Progress (trending up graph)
  FiClipboard, // KPI-Metric (clipboard)
  FiSmile, // Sentiment (smile)
  FiActivity, // Burn-Out (activity/stress)
  FiBell, // Bell icon from Feather Icons
  FiCalendar, // Calendar icon
  FiSun, // Theme icon
  FiMoon,
  FiGrid, // Overview
  FiSettings, // System Settings
} from "react-icons/fi";
import { RxDoubleArrowLeft, RxDoubleArrowRight } from "react-icons/rx";
import { ChakraIcon } from "./ChakraIcon";

// Define the allowed tab types
type TabType =
  | "Users"
  | "Projects"
  | "Tasks"
  | "TimeSheets"
  | "Progress"
  | "KPI-Metric"
  | "Notifications"
  | "Sentiment"
  | "Burn-Out"
  | "Alert"
  | "Calendar"
  | "TimeSheet Approval"
  | "Leave Approval"
  | "profile"
  | "LogPanel"
  | "ManagerBooking"
  | "SeatBooking"
  | "WorkGroup"
  | "Overview"
  | "Departments"
  | "Leave Management"
  | "Approvals"
  | "Team Chats"
  | "My Chats"
  | "SystemLogs"
  | "Help"
  | "Check-Ins"
  | "System Settings";


// Define the prop types for Sidebar
interface SidebarProps {
  isSidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activeTab: TabType;
  setActiveTab: React.Dispatch<React.SetStateAction<TabType>>;
  cardBg: string;
  onLogout: () => void;
  unreadNotifications: number;
  isAdmin: boolean;
  isManagerOrMD: boolean;
  hasApprovalStatus: boolean;
  isEditor: boolean;
  isViewer: boolean;
  canViewSystemSettings: boolean;
  isOrgAdmin: boolean;
  timesheetPendingCount: number;
  orgName: string;
  isAdminView: boolean;
  setIsAdminView: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  isSidebarOpen,
  setSidebarOpen,
  activeTab,
  setActiveTab,
  cardBg,
  onLogout,
  unreadNotifications,
  isAdmin,
  isManagerOrMD,
  hasApprovalStatus,
  isEditor,
  isViewer,
  canViewSystemSettings,
  isOrgAdmin,
  timesheetPendingCount,
  orgName,
  isAdminView,
  setIsAdminView,
  isLoading = false,
}) => {
  const [isAccountExpanded, setIsAccountExpanded] = React.useState(false);
  const { colorMode, toggleColorMode } = useColorMode();
  const isLight = colorMode === "light";

  const renderSkeletons = () => {
    return (
      <VStack align="stretch" spacing={8} px={2} mt={6}>
        {Array.from({ length: 7 }).map((_, index) => (
          <HStack key={index} spacing={3} w="100%">
            <Skeleton
              boxSize="20px"
              borderRadius="md"
              startColor={isLight ? "gray.200" : "gray.700"}
              endColor={isLight ? "gray.300" : "gray.600"}
            />
            {isSidebarOpen && (
              <Skeleton
                h="20px"
                flex="1"
                borderRadius="sm"
                startColor={isLight ? "gray.200" : "gray.700"}
                endColor={isLight ? "gray.300" : "gray.600"}
              />
            )}
          </HStack>
        ))}
      </VStack>
    );
  };

  const getItemStyles = (isActive: boolean) => {
    if (!isLight) {
      return {
        color: isActive ? "zs.accent" : "white",
        bg: isActive ? "zs.cardMuted" : "transparent",
        shadow: isActive ? "sm" : "none",
        position: isActive ? "relative" : undefined,
        outline: "none",
        _focus: { boxShadow: "none" },
        _focusVisible: { boxShadow: "none" },
        _before: isActive
          ? {
            content: '""',
            position: "absolute",
            left: "6px",
            top: "6px",
            bottom: "6px",
            width: "3px",
            background: "linear-gradient(180deg, #FBD282, #B59143)",
            borderRadius: "sm",
          }
          : undefined,
        _hover: { bg: "zs.cardMuted", color: "zs.accent" },
      } as const;
    }
    return {
      color: isActive ? "zs.accent" : "zs.muted",
      bg: isActive ? "white" : "transparent",
      shadow: isActive ? "sm" : "none",
      border: isActive ? "1px solid" : undefined,
      borderColor: isActive ? "zs.border" : undefined,
      borderRadius: "md",
      position: isActive ? "relative" : undefined,
      outline: "none",
      _focus: { boxShadow: "none" },
      _focusVisible: { boxShadow: "none" },
      _before: isActive
        ? {
          content: '""',
          position: "absolute",
          left: "6px",
          top: "6px",
          bottom: "6px",
          width: "3px",
          background: "linear-gradient(180deg, #FBD282, #B59143)",
          borderRadius: "sm",
        }
        : undefined,
      _hover: { bg: isActive ? "white" : "zs.cardMuted", color: "zs.accent" },
    } as const;
  };

  const getIconColor = (isActive: boolean) => {
    if (isActive) return "zs.accent";
    return isLight ? "zs.muted" : "white";
  };

  const scrollbarStyles = isLight
    ? {
      scrollbarColor: "rgb(251, 251, 248) transparent",
      scrollbarWidth: "thin",
      "&::-webkit-scrollbar": {
        width: "4px !important",
        background: "rgba(231, 240, 251, 0.93)",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#F2F6FD",
        borderRadius: "4px",
        border: "1px solid #E7F0FB",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "rgba(209, 217, 228, 0.8)",
      },
    }
    : ({
      scrollbarColor: "#2A3441 transparent",
      scrollbarWidth: "thin",
      "&::-webkit-scrollbar": {
        width: "4px !important",
        background: "#0E1014",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#2A3441",
        borderRadius: "4px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "#3A4553",
      },
    } as const);
  React.useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, [setSidebarOpen]);
  return (
    <Flex
      direction="column"
      bg="zs.sidebar"
      bgGradient={
        colorMode === "dark" ? "linear(to-b, #14161B, #0E1014)" : undefined
      }
      p={4}
      shadow="md"
      transition="width 0.3s"
      position="fixed"
      left={0}
      top={0}
      bottom={0}
      zIndex={10}
      width={isSidebarOpen ? "250px" : "80px"}
    >
      {/* Right gold border for dark mode */}
      {/* {!isLight && (
        <Box
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          width="2px"
          bgGradient={`linear(to-b, ${'#FBD282'}, ${'#B59143'})`}
          borderRadius="sm"
          pointerEvents="none"
        />
      )} */}


      {/* ZenSight Title */}
      {/* Organization Name / Logo Area */}
      <VStack spacing={0} align="center" mb={1} width="100%">
        {isSidebarOpen ? (
          // ONE LINE: Text Only
          <HStack
            spacing={3}
            align="center"
            justify="center"
            w="100%"
            px={2}
            py={1}
          >
            {/* Org Name Text */}
            {orgName ? (
              <Text
                className="font-roboto"
                fontSize="md"
                fontWeight="bold"
                color="zs.text"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                maxW="180px"
                textAlign="center"
              >
                {orgName}
              </Text>
            ) : (
              <Text
                className="font-roboto"
                fontSize="lg"
                fontWeight="bold"
                bgGradient="linear(to-r, #FBD282, #B59143)"
                bgClip="text"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                maxW="180px"
                textAlign="center"
              >
                ZenSight
              </Text>
            )}
          </HStack>
        ) : (
          // Collapsed State: Logo Icon Only
          <Flex
            align="center"
            justify="center"
            bgGradient="linear(to-br, zs.accent, #B59143)"
            color="white"
            borderRadius="lg"
            boxSize="36px"
            fontSize="sm"
            fontWeight="bold"
            boxShadow="md"
            mb={2}
          >
            {orgName ? orgName.substring(0, 2).toUpperCase() : "ZS"}
          </Flex>
        )}
      </VStack>

      <Box
        mt="1px"
        width="100%"
        height="2px"
        bgGradient="linear(to-r, #FBD28233, #B5914333)"
      />

      {/* Sidebar Header with Toggle and optional Notification */}
      <HStack justifyContent="space-between" width="100%" mb={0} mt={2}>
        {/* Sidebar Toggle Button - LEFT */}
        <IconButton
          aria-label="Toggle Sidebar"
          icon={
            isSidebarOpen ? (
              <ChakraIcon as={RxDoubleArrowLeft} boxSize={5} />
            ) : (
              <ChakraIcon as={RxDoubleArrowRight} boxSize={5} />
            )
          }
          onClick={() => setSidebarOpen(!isSidebarOpen)}
          variant="ghost"
          _hover={{}}
          _focus={{ boxShadow: "none" }}
          _focusVisible={{ boxShadow: "none" }}
        />

        {/* View Toggle Button - CENTER - Only for Admins/Managers/Editors */}


        {/* Right Icons - RIGHT */}
        {isSidebarOpen && (
          <HStack spacing={3} className="items-center">
            {/* Theme icon toggle (no color changes) */}
            <div
              className="flex items-center cursor-pointer"
              onClick={toggleColorMode}
            >
              <ChakraIcon
                as={colorMode === "light" ? FiSun : FiMoon}
                boxSize={5}
              />
            </div>
            {/* Notifications */}
            <div className="relative group">
              <div
                className="flex items-center justify-center transition-all duration-200 hover:scale-105 cursor-pointer"
                onClick={() => setActiveTab("Notifications")}
              >
                <ChakraIcon
                  as={FiBell}
                  boxSize={5}
                  color={isLight ? "zs.muted" : "white"}
                />
                {unreadNotifications > 0 && (
                  <div className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-red-500 text-white  flex items-center justify-center border-2 border-white text-[7px]">
                    {unreadNotifications}
                  </div>
                )}
              </div>
            </div>
          </HStack>
        )}
      </HStack>
      <Box
        position={"relative"}
        top={1}
        mt="1px"
        width="100%"
        height="1px"
        bgGradient="linear(to-r, #FBD28233, #B5914333)"
      />

      {/* ORGANIZED SIDEBAR SECTIONS - Scrollable Content Area */}
      <Box
        mt={6}
        mb={2}
        flex="1"
        overflowY="auto"
        minH="0"
        cursor={"pointer"}
        sx={scrollbarStyles}
      >
        {isLoading ? (
          renderSkeletons()
        ) : (
          <>
            {/* ============================================================================
                PROJECTS VIEW (Default)
                Contains: Core Management, Time & Progress
               ============================================================================ */}
            {!isAdminView && (
              <>
                {/* Core Management Section */}
                <VStack align="stretch" spacing={1} mb={4}>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="zs.accent"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    px={2}
                    opacity={isSidebarOpen ? 1 : 0}
                    transition="opacity 0.3s"
                  >
                    {isSidebarOpen && "Core Management"}
                  </Text>

                  {/* Users (Admin Only) */}
                  {isAdmin && (
                    <Button
                      leftIcon={
                        <ChakraIcon
                          as={FiUsers}
                          color={getIconColor(activeTab === "Users")}
                        />
                      }
                      variant="ghost"
                      justifyContent="flex-start"
                      alignItems="center"
                      onClick={() => setActiveTab("Users")}
                      size="sm"
                      {...getItemStyles(activeTab === "Users")}
                    >
                      {isSidebarOpen && (
                        <Text fontSize="13px" fontWeight="semibold" color="inherit">
                          Users
                        </Text>
                      )}
                    </Button>
                  )}

                  {/* Projects */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiLayers}
                        color={getIconColor(activeTab === "Projects")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("Projects")}
                    size="sm"
                    {...getItemStyles(activeTab === "Projects")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        Projects
                      </Text>
                    )}
                  </Button>

                  {/* Tasks */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiList}
                        color={getIconColor(activeTab === "Tasks")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("Tasks")}
                    size="sm"
                    {...getItemStyles(activeTab === "Tasks")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        Tasks
                      </Text>
                    )}
                  </Button>
                </VStack>

                {/* Time & Progress Section */}
                <VStack align="stretch" spacing={1} mb={4}>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="zs.accent"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    px={2}
                    opacity={isSidebarOpen ? 1 : 0}
                    transition="opacity 0.3s"
                  >
                    {isSidebarOpen && "Time & Progress"}
                  </Text>

                  {/* TimeSheets */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiClock}
                        color={getIconColor(activeTab === "TimeSheets")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("TimeSheets")}
                    size="sm"
                    {...getItemStyles(activeTab === "TimeSheets")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        TimeSheets
                      </Text>
                    )}
                  </Button>

                  {/* Progress */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiTrendingUp}
                        color={getIconColor(activeTab === "Progress")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("Progress")}
                    size="sm"
                    {...getItemStyles(activeTab === "Progress")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        Progress
                      </Text>
                    )}
                  </Button>

                  {/* KPI-Metric */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiClipboard}
                        color={getIconColor(activeTab === "KPI-Metric")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("KPI-Metric")}
                    size="sm"
                    {...getItemStyles(activeTab === "KPI-Metric")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        KPI-Metric
                      </Text>
                    )}
                  </Button>

                  {/* Seat Booking (User) */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiLayout}
                        color={getIconColor(activeTab === "SeatBooking")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("SeatBooking")}
                    size="sm"
                    {...getItemStyles(activeTab === "SeatBooking")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        Seat Booking
                      </Text>
                    )}
                  </Button>

                  {/* Calendar (shown to all users) */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiCalendar}
                        color={getIconColor(activeTab === "Calendar")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("Calendar")}
                    size="sm"
                    {...getItemStyles(activeTab === "Calendar")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        Leave Calendar
                      </Text>
                    )}
                  </Button>


                  {/* My Check-Ins */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiUserCheck}
                        color={getIconColor(activeTab === "Check-Ins")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("Check-Ins")}
                    size="sm"
                    {...getItemStyles(activeTab === "Check-Ins")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        My Check-Ins
                      </Text>
                    )}
                  </Button>
                </VStack>

                {/* Chats Section */}
                <VStack align="stretch" spacing={1} mb={4}>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="zs.accent"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    px={2}
                    opacity={isSidebarOpen ? 1 : 0}
                    transition="opacity 0.3s"
                  >
                    {isSidebarOpen && "Chats"}
                  </Text>

                  {/* Team Chats */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiBriefcase}
                        color={getIconColor(activeTab === "Team Chats")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("Team Chats")}
                    size="sm"
                    {...getItemStyles(activeTab === "Team Chats")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        Team Chats
                      </Text>
                    )}
                  </Button>

                  {/* My Chats */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiMessageSquare}
                        color={getIconColor(activeTab === "My Chats")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("My Chats")}
                    size="sm"
                    {...getItemStyles(activeTab === "My Chats")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        My Chats
                      </Text>
                    )}
                  </Button>
                </VStack>

                {/* Help Section */}
                <VStack align="stretch" spacing={1} mb={4}>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="zs.accent"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    px={2}
                    opacity={isSidebarOpen ? 1 : 0}
                    transition="opacity 0.3s"
                  >
                    {isSidebarOpen && "Help"}
                  </Text>

                  {/* Help Documentation */}
                  <Button
                    leftIcon={
                      <ChakraIcon
                        as={FiHelpCircle}
                        color={getIconColor(activeTab === "Help")}
                      />
                    }
                    variant="ghost"
                    justifyContent="flex-start"
                    alignItems="center"
                    onClick={() => setActiveTab("Help")}
                    size="sm"
                    {...getItemStyles(activeTab === "Help")}
                  >
                    {isSidebarOpen && (
                      <Text fontSize="13px" fontWeight="semibold" color="inherit">
                        Help
                      </Text>
                    )}
                  </Button>
                </VStack>
              </>
            )}

            {/* ============================================================================
            ADMINISTRATION VIEW
            Contains: Booking, Analytics, Monitoring, Configuration
           ============================================================================ */}
            {isAdminView && (
              <>
                {/* Booking Section */}
                {isAdmin && (
                  <VStack align="stretch" spacing={1} mb={4}>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="zs.accent"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      px={2}
                      opacity={isSidebarOpen ? 1 : 0}
                      transition="opacity 0.3s"
                    >
                      {isSidebarOpen && "Booking"}
                    </Text>

                    {/* Manager Booking */}
                    <Button
                      leftIcon={
                        <ChakraIcon
                          as={FiBriefcase}
                          color={getIconColor(activeTab === "ManagerBooking")}
                        />
                      }
                      variant="ghost"
                      justifyContent="flex-start"
                      alignItems="center"
                      onClick={() => setActiveTab("ManagerBooking")}
                      size="sm"
                      {...getItemStyles(activeTab === "ManagerBooking")}
                    >
                      {isSidebarOpen && (
                        <Text fontSize="13px" fontWeight="semibold" color="inherit">
                          Manager Booking
                        </Text>
                      )}
                    </Button>
                  </VStack>
                )}

                {/* Analytics Section (only for Admin) */}
                {(isAdmin) && (
                  <VStack align="stretch" spacing={1} mb={4}>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="zs.accent"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      px={2}
                      opacity={isSidebarOpen ? 1 : 0}
                      transition="opacity 0.3s"
                    >
                      {isSidebarOpen && "Analytics"}
                    </Text>

                    {/* Sentiment */}
                    <Button
                      leftIcon={
                        <ChakraIcon
                          as={FiSmile}
                          color={getIconColor(activeTab === "Sentiment")}
                        />
                      }
                      variant="ghost"
                      justifyContent="flex-start"
                      alignItems="center"
                      onClick={() => setActiveTab("Sentiment")}
                      size="sm"
                      {...getItemStyles(activeTab === "Sentiment")}
                    >
                      {isSidebarOpen && (
                        <Text fontSize="13px" fontWeight="semibold" color="inherit">
                          Sentiment
                        </Text>
                      )}
                    </Button>

                    {/* Burn-Out */}
                    <Button
                      leftIcon={
                        <ChakraIcon
                          as={FiActivity}
                          color={getIconColor(activeTab === "Burn-Out")}
                        />
                      }
                      variant="ghost"
                      justifyContent="flex-start"
                      alignItems="center"
                      onClick={() => setActiveTab("Burn-Out")}
                      size="sm"
                      {...getItemStyles(activeTab === "Burn-Out")}
                    >
                      {isSidebarOpen && (
                        <Text fontSize="13px" fontWeight="semibold" color="inherit">
                          Burn-Out
                        </Text>
                      )}
                    </Button>

                  </VStack>
                )}

                {/* Alerts Section (Monitoring) */}
                {(isAdmin || isManagerOrMD || hasApprovalStatus) && (
                  <VStack align="stretch" spacing={1} mb={4}>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="zs.accent"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      px={2}
                      opacity={isSidebarOpen ? 1 : 0}
                      transition="opacity 0.3s"
                    >
                      {isSidebarOpen && "Monitoring"}
                    </Text>

                    {/* Alert */}
                    {isAdmin && (
                      <Button
                        leftIcon={
                          <ChakraIcon
                            as={MdLocalFireDepartment}
                            color={getIconColor(activeTab === "Alert")}
                          />
                        }
                        variant="ghost"
                        justifyContent="flex-start"
                        alignItems="center"
                        onClick={() => setActiveTab("Alert")}
                        size="sm"
                        {...getItemStyles(activeTab === "Alert")}
                      >
                        {isSidebarOpen && (
                          <Text fontSize="13px" fontWeight="semibold" color="inherit">
                            Calendar Alert
                          </Text>
                        )}
                      </Button>
                    )}

                    {/* Overview */}
                    {isAdmin && (
                      <Button
                        leftIcon={
                          <ChakraIcon
                            as={FiGrid}
                            color={getIconColor(activeTab === "Overview")}
                          />
                        }
                        variant="ghost"
                        justifyContent="flex-start"
                        alignItems="center"
                        onClick={() => setActiveTab("Overview")}
                        size="sm"
                        {...getItemStyles(activeTab === "Overview")}
                      >
                        {isSidebarOpen && (
                          <Text fontSize="13px" fontWeight="semibold" color="inherit">
                            Overview
                          </Text>
                        )}
                      </Button>
                    )}

                    {/* Approvals */}
                    <Button
                      leftIcon={
                        <ChakraIcon
                          as={FiCheckSquare}
                          color={getIconColor(activeTab === "Approvals")}
                        />
                      }
                      variant="ghost"
                      justifyContent="flex-start"
                      alignItems="center"
                      onClick={() => setActiveTab("Approvals")}
                      size="sm"
                      {...getItemStyles(activeTab === "Approvals")}
                    >
                      {isSidebarOpen && (
                        <Text fontSize="13px" fontWeight="semibold" color="inherit">
                          Approvals
                        </Text>
                      )}
                    </Button>

                    {/* System Logs */}
                    {isAdmin && (
                      <Button
                        leftIcon={
                          <ChakraIcon
                            as={FiActivity}
                            color={getIconColor(activeTab === "SystemLogs")}
                          />
                        }
                        variant="ghost"
                        justifyContent="flex-start"
                        alignItems="center"
                        onClick={() => setActiveTab("SystemLogs")}
                        size="sm"
                        {...getItemStyles(activeTab === "SystemLogs")}
                      >
                        {isSidebarOpen && (
                          <Text fontSize="13px" fontWeight="semibold" color="inherit">
                            System Logs
                          </Text>
                        )}
                      </Button>
                    )}
                  </VStack>
                )}

                {/* Configuration Section */}
                {isAdmin && (
                  <VStack align="stretch" spacing={1} mb={4}>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="zs.accent"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      px={2}
                      opacity={isSidebarOpen ? 1 : 0}
                      transition="opacity 0.3s"
                    >
                      {isSidebarOpen && "Configuration"}
                    </Text>

                    {/* Work Group */}
                    <Button
                      leftIcon={
                        <ChakraIcon
                          as={FiUsers}
                          color={getIconColor(activeTab === "WorkGroup")}
                        />
                      }
                      variant="ghost"
                      justifyContent="flex-start"
                      alignItems="center"
                      onClick={() => setActiveTab("WorkGroup")}
                      size="sm"
                      {...getItemStyles(activeTab === "WorkGroup")}
                    >
                      {isSidebarOpen && (
                        <Text fontSize="13px" fontWeight="semibold" color="inherit">
                          Work Group
                        </Text>
                      )}
                    </Button>

                    {/* Departments */}
                    <Button
                      leftIcon={
                        <ChakraIcon
                          as={FiList}
                          color={getIconColor(activeTab === "Departments")}
                        />
                      }
                      variant="ghost"
                      justifyContent="flex-start"
                      alignItems="center"
                      onClick={() => setActiveTab("Departments")}
                      size="sm"
                      {...getItemStyles(activeTab === "Departments")}
                    >
                      {isSidebarOpen && (
                        <Text fontSize="13px" fontWeight="semibold" color="inherit">
                          Departments
                        </Text>
                      )}
                    </Button>
                    <Button
                      leftIcon={
                        <ChakraIcon
                          as={HiOutlineClipboardList}
                          color={getIconColor(activeTab === "Leave Management")}
                        />
                      }
                      variant="ghost"
                      justifyContent="flex-start"
                      alignItems="center"
                      onClick={() => setActiveTab("Leave Management")}
                      size="sm"
                      {...getItemStyles(activeTab === "Leave Management")}
                    >
                      {isSidebarOpen && (
                        <Text fontSize="13px" fontWeight="semibold" color="inherit">
                          Leave Management
                        </Text>
                      )}
                    </Button>

                    {/* System Settings */}
                    {canViewSystemSettings && (
                      <Button
                        leftIcon={
                          <ChakraIcon
                            as={FiSettings}
                            color={getIconColor(activeTab === "System Settings")}
                          />
                        }
                        variant="ghost"
                        justifyContent="flex-start"
                        alignItems="center"
                        onClick={() => setActiveTab("System Settings")}
                        size="sm"
                        {...getItemStyles(activeTab === "System Settings")}
                      >
                        {isSidebarOpen && (
                          <Text fontSize="13px" fontWeight="semibold" color="inherit">
                            System Settings
                          </Text>
                        )}
                      </Button>
                    )}
                  </VStack>
                )}
              </>
            )}
          </>
        )}
      </Box>
      {/* ORGANIZED SIDEBAR SECTIONS END */}



      {/* Fixed Bottom Section - LogPanel, Profile, Sign Out */}
      <Box
        mt="auto"
        pt={5}
        pb={2}
        borderTop="1px solid"
        borderRadius={"md"}
        borderColor="zs.border"
        bg="zs.cardMuted"
        mx={-2}
        px={2}
      >
        {isLoading ? (
          <VStack align="stretch" spacing={3} px={2}>
            <HStack spacing={3} w="100%">
              <Skeleton
                boxSize="20px"
                borderRadius="md"
                startColor={isLight ? "gray.200" : "gray.700"}
                endColor={isLight ? "gray.300" : "gray.600"}
              />
              {isSidebarOpen && (
                <Skeleton
                  h="16px"
                  flex="1"
                  borderRadius="sm"
                  startColor={isLight ? "gray.200" : "gray.700"}
                  endColor={isLight ? "gray.300" : "gray.600"}
                />
              )}
            </HStack>
          </VStack>
        ) : (
          <>
            {/* Administration Switcher - Sleek Inline Design */}
            {isSidebarOpen && (isOrgAdmin || isAdmin || hasApprovalStatus) && (
              <Box mb={3} px={1}>
                <Box
                  as="button"
                  onClick={() => {
                    const nextIsAdminView = !isAdminView;
                    setIsAdminView(nextIsAdminView);
                    if (nextIsAdminView) {
                      // Switching TO Administration View
                      if (isAdmin) {
                        setActiveTab("ManagerBooking");
                      } else if (isManagerOrMD || hasApprovalStatus) {
                        setActiveTab("Approvals");
                      } else {
                        setActiveTab("LogPanel");
                      }
                    } else {
                      // Switching TO Projects View
                      if (isAdmin) {
                        setActiveTab("Users");
                      } else {
                        setActiveTab("LogPanel");
                      }
                    }
                  }}
                  w="100%"
                  py={2}
                  px={3}
                  borderRadius="md"
                  border="1px solid"
                  borderColor={isLight ? "gray.200" : "whiteAlpha.200"}
                  bg={isLight ? "white" : "whiteAlpha.50"}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: "zs.accent",
                    bg: isLight ? "gray.50" : "whiteAlpha.100",
                    transform: "translateY(-1px)",
                    shadow: "sm"
                  }}
                  _active={{
                    transform: "translateY(0)",
                  }}
                >
                  <HStack spacing={3}>
                    <Box
                      p={1.5}
                      borderRadius="md"
                      bgGradient="linear(to-br, #FBD282, #B59143)"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <ChakraIcon as={FiRepeat} boxSize={3.5} />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" fontWeight="bold" color="zs.text">
                        {isAdminView ? "Admin Console" : "Project Space"}
                      </Text>
                      <Text fontSize="10px" color="zs.muted">
                        Switch to {isAdminView ? "Projects" : "Admin"}
                      </Text>
                    </VStack>
                  </HStack>
                  <ChakraIcon as={FiChevronRight} color="zs.muted" boxSize={4} />
                </Box>
              </Box>
            )}
            {/* Bottom Section Header with Expand/Collapse Toggle */}
            <HStack
              as="button"
              onClick={() => setIsAccountExpanded((v) => !v)}
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              px={2}
              mb={2}
              _focus={{ boxShadow: "none", outline: "none" }}
              _focusVisible={{ boxShadow: "none", outline: "none" }}
            >
              {isSidebarOpen && (
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="zs.accent"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Account & Logs
                </Text>
              )}
              <ChakraIcon
                as={isAccountExpanded ? FiChevronDown : FiChevronUp}
                color="zs.accent"
                boxSize={4}
              />
            </HStack>

            <VStack align="stretch" spacing={1}>
              {/* LogPanel Button */}
              <Button
                leftIcon={
                  <ChakraIcon
                    as={MdHistory}
                    color={getIconColor(activeTab === "LogPanel")}
                  />
                }
                variant="ghost"
                justifyContent="flex-start"
                alignItems="center"
                onClick={() => setActiveTab("LogPanel")}
                size="sm"
                w="100%"
                {...getItemStyles(activeTab === "LogPanel")}
              >
                {isSidebarOpen && (
                  <Text fontSize="13px" fontWeight="semibold" color="inherit">
                    Employee Logs
                  </Text>
                )}
              </Button>

              {/* Profile Button */}
              {isAccountExpanded && (
                <Button
                  leftIcon={
                    <ChakraIcon
                      as={FiUserCheck}
                      color={getIconColor(activeTab === "profile")}
                    />
                  }
                  variant="ghost"
                  justifyContent="flex-start"
                  alignItems="center"
                  onClick={() => setActiveTab("profile")}
                  size="sm"
                  w="100%"
                  {...getItemStyles(activeTab === "profile")}
                >
                  {isSidebarOpen && (
                    <Text fontSize="13px" fontWeight="semibold" color="inherit">
                      Profile
                    </Text>
                  )}
                </Button>
              )}

              {/* Sign Out Button */}
              {isAccountExpanded && (
                <Button
                  leftIcon={
                    <ChakraIcon as={FiLogOut} color={getIconColor(false)} />
                  }
                  variant="ghost"
                  justifyContent="flex-start"
                  alignItems="center"
                  onClick={onLogout}
                  size="sm"
                  w="100%"
                  {...getItemStyles(false)}
                >
                  {isSidebarOpen && (
                    <Text fontSize="13px" fontWeight="semibold" color="inherit">
                      Sign Out
                    </Text>
                  )}
                </Button>
              )}
            </VStack>
          </>
        )}
      </Box>

      {/* ZenSight Title - Moved to Bottom */}
      <VStack spacing={0} align="center" mt={1} mb={1} width="100%">
        {/* ZenSight Title (Bottom, Smaller, Gradient) */}
        <Flex
          alignItems="center"
          justifyContent="center"
          px={isSidebarOpen ? 1 : 0}
          mt={isSidebarOpen ? 0 : 0}
          width="100%"
        >
          {isSidebarOpen ? (
            // Full ZenSight Title (Small) with standard lines
            <HStack width="100%" spacing={4} alignItems="center">
              <Box h="1px" flex="1" bgGradient="linear(to-r, transparent, #FBD282)" opacity={0.6} />
              <Flex alignItems="center">
                <Text
                  className="font-roboto font-bold"
                  fontSize="md"
                  color="zs.accent"
                  fontWeight="700"
                  display="inline"
                  style={{
                    background: "linear-gradient(45deg, #FBD282, #B59143)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Zen
                </Text>
                <Text
                  fontFamily="'Roboto', sans-serif"
                  fontSize="md"
                  color="zs.text"
                  className="font-roboto font-bold"
                  fontWeight="700"
                  display="inline"
                >
                  Sight
                </Text>
              </Flex>
              <Box h="1px" flex="1" bgGradient="linear(to-l, transparent, #FBD282)" opacity={0.6} />
            </HStack>
          ) : (
            // Collapsed State (ZS)
            <>
              <Text
                className="font-roboto text-md font-bold"
                color="zs.accent"
                fontWeight="600"
                display="inline"
                style={{
                  background: "linear-gradient(45deg, #FBD282, #B59143)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Z
              </Text>
              <Text
                fontFamily="'Roboto', sans-serif"
                color="zs.text"
                className="font-roboto text-md font-bold"
                fontWeight="600"
                display="inline"
              >
                S
              </Text>
            </>
          )}
        </Flex>
      </VStack>
    </Flex>
  );
};

export default Sidebar;
